import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit2, Trash2, HelpCircle, Search,
  ChevronDown, Tag, Eye, ToggleRight,
  Loader2, X, BookOpen, RefreshCw, Filter,
  MessageSquare, Sparkles, Bot, Hash,
  CheckCircle2, AlertCircle, Copy, Star,
  TrendingUp, Zap, ThumbsUp, ThumbsDown,
  MoreVertical, Archive, RotateCcw,
} from 'lucide-react';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch }   from '@/components/ui/switch';
import { Badge }    from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api       from '@/lib/api';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'general',   label: 'General',        emoji: '📋', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'pricing',   label: 'Pricing',         emoji: '💰', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'shipping',  label: 'Shipping',        emoji: '🚚', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'support',   label: 'Support',         emoji: '🎧', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  { value: 'product',   label: 'Product',         emoji: '📦', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'returns',   label: 'Returns',         emoji: '↩️', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  { value: 'account',   label: 'Account',         emoji: '👤', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { value: 'payment',   label: 'Payment',         emoji: '💳', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map(c => [c.value, c]));

// Pre-built FAQ starters
const STARTER_FAQS = [
  {
    category: 'pricing',
    question: 'What are your pricing plans?',
    answer: 'We offer three plans: Starter (Free), Pro ($49/month), and Business ($149/month). All plans include a 14-day free trial with no credit card required. Enterprise pricing is available on request.',
  },
  {
    category: 'shipping',
    question: 'How long does delivery take?',
    answer: 'Standard delivery takes 3-5 business days. Express delivery (1-2 days) is available for an additional charge. Free shipping on orders above ₹499.',
  },
  {
    category: 'returns',
    question: 'What is your return policy?',
    answer: 'We accept returns within 30 days of purchase. Items must be unused and in original packaging. Refunds are processed within 5-7 business days after we receive the return.',
  },
  {
    category: 'support',
    question: 'How can I contact customer support?',
    answer: 'You can reach us via WhatsApp (fastest), email at support@truechattr.com, or call us at +91 98765 43210. Our support hours are Monday–Friday, 9 AM – 6 PM IST.',
  },
  {
    category: 'account',
    question: 'How do I reset my password?',
    answer: 'Click "Forgot Password" on the login page, enter your email, and follow the instructions sent to your inbox. The reset link is valid for 24 hours.',
  },
  {
    category: 'payment',
    question: 'What payment methods do you accept?',
    answer: 'We accept UPI, credit/debit cards (Visa, Mastercard, RuPay), net banking, and popular wallets (Paytm, PhonePe, Google Pay). All payments are secured with 256-bit SSL encryption.',
  },
];

const EMPTY_FORM = {
  question: '',
  answer: '',
  category: 'general',
  isActive: true,
  tags: '',
  priority: 'normal',
};

const fadeUp = (delay = 0) => ({
  initial:    { opacity: 0, y: 16 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1], delay },
});

// ─────────────────────────────────────────────────────────────────────────────
// WHATSAPP MESSAGE PREVIEW
// ─────────────────────────────────────────────────────────────────────────────

function WAPreview({ question, answer }) {
  const now = new Date();
  const time = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    <div className="bg-[#ECE5DD] rounded-2xl p-4 space-y-2"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23b2dfdb' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}
    >
      {/* Customer question bubble */}
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-[#DCF8C6] rounded-2xl rounded-tr-sm px-3 py-2 shadow-sm">
          <p className="text-xs text-gray-800">{question || 'Customer question here...'}</p>
          <div className="flex justify-end mt-1">
            <span className="text-[10px] text-gray-400">{time}</span>
          </div>
        </div>
      </div>

      {/* Bot answer bubble */}
      <div className="flex justify-start gap-2">
        <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-1">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <div className="max-w-[80%] bg-white rounded-2xl rounded-tl-sm px-3 py-2 shadow-sm">
          <p className="text-[10px] text-emerald-600 font-semibold mb-1">TrueChattr Bot</p>
          <p className="text-xs text-gray-800 leading-relaxed">{answer || 'Bot answer will appear here...'}</p>
          <div className="flex items-center justify-between mt-2 pt-1 border-t border-gray-100">
            <div className="flex gap-2">
              <button className="text-[10px] text-[#128C7E] font-medium">👍 Helpful</button>
              <button className="text-[10px] text-gray-400">Talk to agent</button>
            </div>
            <span className="text-[10px] text-gray-400">{time}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FAQ CARD
// ─────────────────────────────────────────────────────────────────────────────

function FAQCard({ faq, onEdit, onDelete, onDuplicate, onToggle, isDeleting, index }) {
  const [expanded, setExpanded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const cat = CAT_MAP[faq.category] || CAT_MAP.general;

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.35, delay: index * 0.04 }}
      className={`group bg-white rounded-2xl border overflow-hidden transition-all duration-300 ${
        faq.is_active
          ? 'border-gray-100 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-50'
          : 'border-gray-100 opacity-60'
      } shadow-sm`}
    >
      {/* Category color bar */}
      <div className={`h-1 w-full ${
        faq.category === 'pricing'  ? 'bg-gradient-to-r from-emerald-400 to-teal-400' :
        faq.category === 'shipping' ? 'bg-gradient-to-r from-blue-400 to-cyan-400' :
        faq.category === 'support'  ? 'bg-gradient-to-r from-violet-400 to-purple-400' :
        faq.category === 'product'  ? 'bg-gradient-to-r from-orange-400 to-amber-400' :
        faq.category === 'returns'  ? 'bg-gradient-to-r from-pink-400 to-rose-400' :
        faq.category === 'account'  ? 'bg-gradient-to-r from-indigo-400 to-blue-400' :
        faq.category === 'payment'  ? 'bg-gradient-to-r from-yellow-400 to-orange-400' :
        'bg-gradient-to-r from-gray-300 to-gray-400'
      }`} />

      <div className="p-5">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
              expanded ? 'bg-emerald-500' : 'bg-emerald-50'
            }`}>
              <HelpCircle className={`w-4 h-4 ${expanded ? 'text-white' : 'text-emerald-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-2">
                {faq.question}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cat.color}`}>
                  <span>{cat.emoji}</span> {cat.label}
                </span>
                {!faq.is_active && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 border border-gray-200">
                    Inactive
                  </span>
                )}
                {faq.priority === 'high' && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
                    <Star className="w-2.5 h-2.5" /> Priority
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Stats */}
            <div className="hidden group-hover:flex items-center gap-1 mr-1">
              <span className="flex items-center gap-1 text-[11px] text-gray-400">
                <Eye className="w-3 h-3" /> {faq.view_count || 0}
              </span>
            </div>

            {/* Quick toggle */}
            <button
              onClick={() => onToggle(faq)}
              title={faq.is_active ? 'Disable' : 'Enable'}
              className={`p-1.5 rounded-lg transition-colors ${
                faq.is_active
                  ? 'text-emerald-500 hover:bg-emerald-50'
                  : 'text-gray-400 hover:bg-gray-100'
              }`}
            >
              <Zap className="w-3.5 h-3.5" />
            </button>

            {/* 3-dot menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(v => !v)}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
              <AnimatePresence>
                {menuOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -5 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -5 }}
                    className="absolute right-0 top-8 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-20 overflow-hidden"
                  >
                    {[
                      { icon: Edit2,   label: 'Edit',      action: () => { onEdit(faq); setMenuOpen(false); }, color: 'text-gray-700' },
                      { icon: Copy,    label: 'Duplicate', action: () => { onDuplicate(faq); setMenuOpen(false); }, color: 'text-blue-600' },
                      { icon: Eye,     label: 'Preview',   action: () => { setShowPreview(v => !v); setMenuOpen(false); }, color: 'text-violet-600' },
                      { icon: Trash2,  label: 'Delete',    action: () => { onDelete(faq.id); setMenuOpen(false); }, color: 'text-red-500' },
                    ].map(item => (
                      <button
                        key={item.label}
                        onClick={item.action}
                        disabled={item.label === 'Delete' && isDeleting}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-medium hover:bg-gray-50 transition-colors ${item.color} ${item.label === 'Delete' ? 'border-t border-gray-100' : ''}`}
                      >
                        {item.label === 'Delete' && isDeleting
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <item.icon className="w-3.5 h-3.5" />
                        }
                        {item.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 mb-3">
          <span className="flex items-center gap-1 text-[11px] text-gray-400">
            <Eye className="w-3 h-3" /> {faq.view_count || 0} views
          </span>
          {faq.helpful_count > 0 && (
            <span className="flex items-center gap-1 text-[11px] text-emerald-600">
              <ThumbsUp className="w-3 h-3" /> {faq.helpful_count} helpful
            </span>
          )}
          {faq.tags && (
            <div className="flex items-center gap-1 flex-wrap">
              {faq.tags.split(',').slice(0, 2).map(tag => (
                <span key={tag} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                  <Hash className="w-2.5 h-2.5" />{tag.trim()}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Answer preview */}
        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-3">
          {faq.answer}
        </p>

        {/* Expand / Preview row */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(v => !v)}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            {expanded ? 'Collapse' : 'Read full answer'}
          </button>
          <button
            onClick={() => setShowPreview(v => !v)}
            className={`flex items-center gap-1 py-1.5 px-3 text-xs rounded-lg transition-all ${
              showPreview
                ? 'bg-emerald-50 text-emerald-600'
                : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            WhatsApp
          </button>
        </div>

        {/* Expanded answer */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="pt-3 mt-3 border-t border-gray-100">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{faq.answer}</p>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => onEdit(faq)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                  >
                    <Edit2 className="w-3 h-3" /> Edit
                  </button>
                  <button
                    onClick={() => onDuplicate(faq)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-all"
                  >
                    <Copy className="w-3 h-3" /> Duplicate
                  </button>
                  <button
                    onClick={() => onDelete(faq.id)}
                    disabled={isDeleting}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:border-rose-300 hover:text-rose-600 hover:bg-rose-50 transition-all disabled:opacity-50 ml-auto"
                  >
                    {isDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* WhatsApp preview */}
        <AnimatePresence>
          {showPreview && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="pt-3 mt-3 border-t border-gray-100">
                <p className="text-[11px] text-gray-400 font-medium mb-2 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> WhatsApp Preview
                </p>
                <WAPreview question={faq.question} answer={faq.answer} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function FAQs() {
  const [faqs,          setFaqs]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [catFilter,     setCatFilter]     = useState('all');
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [dialogOpen,    setDialogOpen]    = useState(false);
  const [starterOpen,   setStarterOpen]   = useState(false);
  const [editingFaq,    setEditingFaq]    = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [deletingId,    setDeletingId]    = useState(null);
  const [formData,      setFormData]      = useState(EMPTY_FORM);
  const [previewOpen,   setPreviewOpen]   = useState(false);
  const [viewMode,      setViewMode]      = useState('grid'); // grid | list

  useEffect(() => { fetchFaqs(); }, []);

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/business/faqs');
      setFaqs(data.faqs || []);
    } catch {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  // ─── stats ────────────────────────────────────────────────────────────────
  const activeCount = faqs.filter(f => f.is_active).length;
  const totalViews  = faqs.reduce((s, f) => s + (f.view_count || 0), 0);
  const totalHelpful = faqs.reduce((s, f) => s + (f.helpful_count || 0), 0);
  const categoryCounts = CATEGORIES.map(c => ({
    ...c,
    count: faqs.filter(f => f.category === c.value).length,
  })).filter(c => c.count > 0);

  // ─── filtered list ─────────────────────────────────────────────────────────
  const filtered = faqs.filter(f => {
    const matchSearch = f.question.toLowerCase().includes(search.toLowerCase()) ||
                        f.answer.toLowerCase().includes(search.toLowerCase()) ||
                        (f.tags || '').toLowerCase().includes(search.toLowerCase());
    const matchCat    = catFilter === 'all' || f.category === catFilter;
    const matchStatus = statusFilter === 'all'
      ? true
      : statusFilter === 'active'
        ? f.is_active
        : !f.is_active;
    return matchSearch && matchCat && matchStatus;
  });

  // ─── save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (formData.question.trim().length < 5) { toast.error('Question too short (min 5 chars)'); return; }
    if (formData.answer.trim().length < 5)   { toast.error('Answer too short (min 5 chars)'); return; }

    setSaving(true);
    try {
      const payload = {
        question:  formData.question.trim(),
        answer:    formData.answer.trim(),
        category:  formData.category,
        isActive:  formData.isActive,
        tags:      formData.tags?.trim() || '',
        priority:  formData.priority || 'normal',
      };

      if (editingFaq) {
        await api.put(`/business/faqs/${editingFaq.id}`, payload);
        toast.success('FAQ updated!');
      } else {
        await api.post('/business/faqs', payload);
        toast.success('FAQ created!');
      }
      closeDialog();
      fetchFaqs();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  // ─── delete ───────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/business/faqs/${id}`);
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  // ─── toggle active ────────────────────────────────────────────────────────
  const handleToggle = async (faq) => {
    try {
      await api.put(`/business/faqs/${faq.id}`, {
        question: faq.question,
        answer: faq.answer,
        category: faq.category,
        isActive: !faq.is_active,
      });
      toast.success(`FAQ ${!faq.is_active ? 'enabled' : 'disabled'}`);
      fetchFaqs();
    } catch {
      toast.error('Failed to update');
    }
  };

  // ─── duplicate ────────────────────────────────────────────────────────────
  const handleDuplicate = (faq) => {
    setEditingFaq(null);
    setFormData({
      question: `${faq.question} (Copy)`,
      answer:   faq.answer,
      category: faq.category,
      isActive: true,
      tags:     faq.tags || '',
      priority: faq.priority || 'normal',
    });
    setDialogOpen(true);
  };

  // ─── load starter ─────────────────────────────────────────────────────────
  const loadStarter = (starter) => {
    setEditingFaq(null);
    setFormData({
      question: starter.question,
      answer:   starter.answer,
      category: starter.category,
      isActive: true,
      tags: '',
      priority: 'normal',
    });
    setStarterOpen(false);
    setDialogOpen(true);
  };

  // ─── dialog helpers ───────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingFaq(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer:   faq.answer,
      category: faq.category || 'general',
      isActive: faq.is_active,
      tags:     faq.tags || '',
      priority: faq.priority || 'normal',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingFaq(null);
    setFormData(EMPTY_FORM);
  };

  // ─── loading ──────────────────────────────────────────────────────────────
  if (loading && !faqs.length) {
    return (
      <div className="h-[calc(100vh-140px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-14 h-14">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-100" />
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Loading FAQs…</p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8" data-testid="faqs-page">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0)} className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
            <HelpCircle className="w-7 h-7 text-emerald-600" />
            FAQ Manager
          </h1>
          <p className="mt-1 text-sm text-gray-400">
            Train your AI bot with questions & answers — {faqs.length} total
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={fetchFaqs}
            disabled={loading}
            className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setStarterOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 transition-all"
          >
            <Sparkles className="w-4 h-4" />
            Use Starter
          </button>
          <button
            onClick={openAdd}
            data-testid="add-faq-button"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 transition-all"
          >
            <Plus className="w-4 h-4" />
            Add FAQ
          </button>
        </div>
      </motion.div>

      {/* ── Stats ───────────────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.06)} className="flex flex-wrap gap-3">
        {[
          { icon: BookOpen,    label: 'Total FAQs',    value: faqs.length    },
          { icon: Zap,         label: 'Active',         value: activeCount    },
          { icon: Eye,         label: 'Total Views',    value: totalViews     },
          { icon: ThumbsUp,    label: 'Marked Helpful', value: totalHelpful   },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-center gap-2.5 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Icon className="w-3.5 h-3.5 text-emerald-600" />
            </div>
            <div>
              <span className="text-xs text-gray-400 block leading-none mb-0.5">{label}</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">{value}</span>
            </div>
          </div>
        ))}
      </motion.div>

      {/* ── Category chips ───────────────────────────────────────────────────── */}
      {categoryCounts.length > 0 && (
        <motion.div {...fadeUp(0.08)} className="flex flex-wrap gap-2">
          <button
            onClick={() => setCatFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              catFilter === 'all'
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'
            }`}
          >
            All ({faqs.length})
          </button>
          {categoryCounts.map(cat => (
            <button
              key={cat.value}
              onClick={() => setCatFilter(catFilter === cat.value ? 'all' : cat.value)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                catFilter === cat.value
                  ? `${cat.color} border-current`
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
            >
              <span>{cat.emoji}</span> {cat.label} <span className="opacity-60">({cat.count})</span>
            </button>
          ))}
        </motion.div>
      )}

      {/* ── Search + Filters ─────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.1)} className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            placeholder="Search FAQs…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-10 rounded-xl border-gray-200 text-sm">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">✅ Active</SelectItem>
            <SelectItem value="inactive">⏸ Inactive</SelectItem>
          </SelectContent>
        </Select>

        {(search || catFilter !== 'all' || statusFilter !== 'all') && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
            <button
              onClick={() => { setSearch(''); setCatFilter('all'); setStatusFilter('all'); }}
              className="text-xs text-emerald-600 hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </motion.div>

      {/* ── AI Bot Info Banner ───────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.12)} className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
        <Bot className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">AI Bot Training</p>
          <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">
            Active FAQs are used to train your WhatsApp AI bot. When customers ask similar questions, the bot will respond automatically. Higher view count = more popular questions.
          </p>
        </div>
        <div className="ml-auto flex-shrink-0">
          <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-lg">
            {activeCount} active
          </span>
        </div>
      </motion.div>

      {/* ── FAQ Grid ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="faqs-list">
        <AnimatePresence>
          {filtered.length > 0 ? (
            filtered.map((faq, index) => (
              <FAQCard
                key={faq.id}
                faq={faq}
                index={index}
                onEdit={openEdit}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                onToggle={handleToggle}
                isDeleting={deletingId === faq.id}
              />
            ))
          ) : (
            <motion.div
              key="empty"
              {...fadeUp(0.1)}
              className="col-span-full text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm"
            >
              <div className="w-20 h-20 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-5">
                <HelpCircle className="w-10 h-10 text-emerald-300" />
              </div>
              <p className="text-gray-900 dark:text-white font-bold text-lg mb-2">
                {search || catFilter !== 'all' || statusFilter !== 'all' ? 'No FAQs found' : 'No FAQs yet'}
              </p>
              <p className="text-sm text-gray-400 mb-6 max-w-sm mx-auto">
                {search
                  ? `No results for "${search}"`
                  : 'Add FAQs to train your AI bot. The more FAQs you add, the smarter your bot becomes.'}
              </p>
              {!search && catFilter === 'all' && (
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setStarterOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 transition-colors"
                  >
                    <Sparkles className="w-4 h-4" /> Use Starters
                  </button>
                  <button
                    onClick={openAdd}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Add FAQ
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          STARTER FAQs MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {starterOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setStarterOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-gray-200 dark:border-gray-700"
            >
              <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-600" /> Starter FAQs
                  </h2>
                  <p className="text-sm text-gray-400 mt-0.5">Pick a pre-written FAQ to get started quickly</p>
                </div>
                <button onClick={() => setStarterOpen(false)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)] space-y-3">
                {STARTER_FAQS.map((starter, i) => {
                  const cat = CAT_MAP[starter.category];
                  return (
                    <button
                      key={i}
                      onClick={() => loadStarter(starter)}
                      className="w-full text-left p-4 rounded-xl border-2 border-gray-100 hover:border-emerald-300 hover:shadow-md hover:bg-emerald-50/30 transition-all duration-200 group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                          <span className="text-lg">{cat?.emoji}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cat?.color}`}>
                              {cat?.label}
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-emerald-700 mb-1">{starter.question}</p>
                          <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">{starter.answer}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-emerald-500 flex-shrink-0 mt-1 transition-colors" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          ADD / EDIT DIALOG
      ═══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={dialogOpen} onOpenChange={open => !open && closeDialog()}>
        <DialogContent className="sm:max-w-4xl max-h-[95vh] overflow-hidden p-0 rounded-2xl border-gray-100 shadow-2xl">
          <div className="flex flex-col h-full max-h-[95vh]">

            <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-emerald-600" />
                {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-1 overflow-hidden min-h-0">

              {/* ── Left: Form ──────────────────────────────────────────────── */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5 border-r border-gray-100">

                {/* Question */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Question <span className="text-rose-400">*</span>
                  </Label>
                  <Input
                    value={formData.question}
                    onChange={e => setFormData({ ...formData, question: e.target.value })}
                    placeholder="What are your business hours?"
                    data-testid="faq-question-input"
                    className="h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
                  />
                  <p className="text-[11px] text-gray-400">{formData.question.length}/300 characters</p>
                </div>

                {/* Answer */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Answer <span className="text-rose-400">*</span>
                    <span className="ml-2 text-xs font-normal text-gray-400">{formData.answer.length}/1000</span>
                  </Label>
                  <Textarea
                    value={formData.answer}
                    onChange={e => setFormData({ ...formData, answer: e.target.value })}
                    placeholder="We're open Monday to Friday, 9 AM – 6 PM IST. On weekends, our AI bot is available 24/7 for instant support."
                    rows={6}
                    maxLength={1000}
                    data-testid="faq-answer-input"
                    className="rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm resize-none leading-relaxed"
                  />
                </div>

                {/* Category + Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category</Label>
                    <Select value={formData.category} onValueChange={v => setFormData({ ...formData, category: v })}>
                      <SelectTrigger className="h-10 rounded-xl border-gray-200 text-sm" data-testid="faq-category-input">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Priority</Label>
                    <Select value={formData.priority} onValueChange={v => setFormData({ ...formData, priority: v })}>
                      <SelectTrigger className="h-10 rounded-xl border-gray-200 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">📋 Normal</SelectItem>
                        <SelectItem value="high">⭐ High Priority</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Tags */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Tags <span className="text-xs font-normal text-gray-400">(comma-separated)</span>
                  </Label>
                  <Input
                    value={formData.tags}
                    onChange={e => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="delivery, timing, hours"
                    className="h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
                  />
                  {formData.tags && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.tags.split(',').filter(Boolean).map(tag => (
                        <span key={tag} className="inline-flex items-center gap-0.5 text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          <Hash className="w-2.5 h-2.5" />{tag.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Active toggle */}
                <div className="flex items-center justify-between p-3.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</p>
                    <p className="text-xs text-gray-400">AI bot will use this FAQ to answer customers</p>
                  </div>
                  <Switch
                    checked={formData.isActive}
                    onCheckedChange={v => setFormData({ ...formData, isActive: v })}
                    data-testid="faq-enabled-toggle"
                    className="data-[state=checked]:bg-emerald-500"
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={closeDialog}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-faq-button"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 disabled:opacity-60 transition-all"
                  >
                    {saving
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                      : editingFaq
                        ? <><CheckCircle2 className="w-4 h-4" /> Update FAQ</>
                        : <><Plus className="w-4 h-4" /> Add FAQ</>
                    }
                  </button>
                </div>
              </div>

              {/* ── Right: Live Preview ──────────────────────────────────── */}
              <div className="hidden lg:flex w-[360px] flex-shrink-0 bg-gray-50 dark:bg-gray-800/50 flex-col p-6 overflow-y-auto gap-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">WhatsApp Preview</p>
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">Live</span>
                </div>

                <WAPreview question={formData.question} answer={formData.answer} />

                {/* Info panel */}
                <div className="w-full p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 space-y-3">
                  <p className="text-xs font-semibold text-gray-500">FAQ Info</p>
                  <div className="space-y-2">
                    {[
                      { label: 'Question length', value: `${formData.question.length}/300` },
                      { label: 'Answer length',   value: `${formData.answer.length}/1000` },
                      { label: 'Category',        value: CAT_MAP[formData.category]?.label || '—' },
                      { label: 'Priority',        value: formData.priority === 'high' ? '⭐ High' : '📋 Normal' },
                      { label: 'Status',          value: formData.isActive ? '✅ Active' : '⏸ Inactive' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex justify-between text-xs">
                        <span className="text-gray-400">{label}</span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tips */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" /> Tips for better FAQs
                  </p>
                  <ul className="space-y-1.5 text-xs text-amber-700">
                    <li>• Keep answers concise and clear</li>
                    <li>• Use customer's language in questions</li>
                    <li>• Add tags for better bot matching</li>
                    <li>• Mark frequently asked ones as High Priority</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Missing import fix
const ArrowRight = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);